import { useMemo } from 'react';
import type { PermissionState } from './permissionState';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';

export interface BulkActionGlobalSettings {
  enableCopyPreviousWeek: boolean;
  copyDescriptions: boolean;
  copyOnlyBillable: boolean;
  enableAutoSmartFill: boolean;
  enableAiInsights: boolean;
}

export interface UseBulkActionsOptions {
  permissions: PermissionState;
  setAllPermissions: (permissions: Partial<PermissionState>) => void;
  onPermissionChange: () => void;
  globalSettings: BulkActionGlobalSettings;
  /** Only the Administrator platform role must always retain manageRolesPermissions */
  isAdministrator: boolean;
}

export interface BulkActions {
  handleSelectAllTimeTracking: () => void;
  handleDeselectAllTimeTracking: () => void;
  handleSelectAllApproval: () => void;
  handleDeselectAllApproval: () => void;
  handleSelectAllReports: () => void;
  handleDeselectAllReports: () => void;
  handleSelectAllManagement: () => void;
  handleDeselectAllManagement: () => void;
  handleSelectAllDataIntegration: () => void;
  handleDeselectAllDataIntegration: () => void;
  handleSelectAllAdministration: () => void;
  handleDeselectAllAdministration: () => void;
}

/**
 * Per-tab select all / deselect all handlers. Each applies its whole category in
 * a single SET_ALL dispatch.
 */
export const useBulkActions = ({
  permissions,
  setAllPermissions,
  onPermissionChange,
  globalSettings,
  isAdministrator,
}: UseBulkActionsOptions): BulkActions => {
  const { enabledPermissions } = permissions;
  const { auditTrail, postingLog } = useFeatureFlags();

  return useMemo(() => {
    const apply = (next: Partial<PermissionState>) => {
      setAllPermissions(next);
      onPermissionChange();
    };

    // Reports hidden by a feature flag keep their stored values — bulk actions skip them.
    const withoutHiddenReports = (next: Partial<PermissionState>): Partial<PermissionState> => {
      const { enableAuditLog, allowAuditLogExport, enablePostingLog, allowPostingLogExport, allowPostingLogRetry, ...rest } = next;
      return {
        ...rest,
        ...(auditTrail ? { enableAuditLog, allowAuditLogExport } : {}),
        ...(postingLog ? { enablePostingLog, allowPostingLogExport, allowPostingLogRetry } : {}),
      };
    };

    const withHomepagePermission = (enabled: boolean): Set<string> => {
      const next = new Set(enabledPermissions);
      if (enabled) next.add('dashboard.view_own');
      else next.delete('dashboard.view_own');
      return next;
    };

    return {
      handleSelectAllTimeTracking: () => apply({
        enabledPermissions: withHomepagePermission(true),
        allowRecallTimesheet: true,
        allowDuplicateTimesheets: true,
        allowDeleteTimesheets: true,
        enableCopyFromLastWeek: globalSettings.enableCopyPreviousWeek,
        enableCopyDescription: globalSettings.copyDescriptions,
        enableCopyOnlyBillable: globalSettings.copyOnlyBillable,
        enableAutoSmartFill: globalSettings.enableAutoSmartFill,
        enableAiInsights: globalSettings.enableAiInsights,
      }),

      handleDeselectAllTimeTracking: () => apply({
        enabledPermissions: withHomepagePermission(false),
        allowRecallTimesheet: false,
        allowDuplicateTimesheets: false,
        allowDeleteTimesheets: false,
        enableCopyFromLastWeek: false,
        enableCopyDescription: false,
        enableCopyOnlyBillable: false,
        enableAutoSmartFill: false,
        enableAiInsights: false,
      }),

      handleSelectAllApproval: () => apply({
        viewApprovalDashboard: true,
        viewApprovals: true,
        editTimesheetsApproval: true,
        rejectTimesheets: true,
        compulsoryRejectionReason: true,
        bulkApprove: true,
        delegateApproval: true,
        viewPolicyViolations: true,
        viewDetectedIssues: true,
      }),

      handleDeselectAllApproval: () => apply({
        viewApprovalDashboard: false,
        viewApprovals: false,
        editTimesheetsApproval: false,
        rejectTimesheets: false,
        compulsoryRejectionReason: false,
        bulkApprove: false,
        delegateApproval: false,
        viewPolicyViolations: false,
        viewDetectedIssues: false,
      }),

      // billableHoursReport removed — belongs in the Projects module of the PSA suite.
      handleSelectAllReports: () => apply(withoutHiddenReports({
        enableTimesheetReport: true,
        allowTimesheetReportExport: true,
        enableMissingTimesheetReport: true,
        allowMissingTimesheetReportExport: true,
        enableUtilisationReport: true,
        allowUtilisationReportExport: true,
        enableAuditLog: true,
        allowAuditLogExport: true,
        enablePostingLog: true,
        allowPostingLogExport: true,
        allowPostingLogRetry: true,
      })),

      handleDeselectAllReports: () => apply(withoutHiddenReports({
        enableTimesheetReport: false,
        allowTimesheetReportExport: false,
        enableMissingTimesheetReport: false,
        allowMissingTimesheetReportExport: false,
        enableUtilisationReport: false,
        allowUtilisationReportExport: false,
        enableAuditLog: false,
        allowAuditLogExport: false,
        enablePostingLog: false,
        allowPostingLogExport: false,
        allowPostingLogRetry: false,
      })),

      handleSelectAllManagement: () => apply({
        viewEmployeeDirectory: true,
        viewEmployeeDirectoryAccess: 'all',
        viewEmployeeDetails: true,
      }),

      handleDeselectAllManagement: () => apply({
        viewEmployeeDirectory: false,
        viewEmployeeDirectoryAccess: 'own',
        viewEmployeeDetails: false,
      }),

      handleSelectAllDataIntegration: () => apply({
        viewIntegrations: true,
        manageIntegrations: true,
        viewCompanies: true,
        manageCompanies: true,
      }),

      handleDeselectAllDataIntegration: () => apply({
        viewIntegrations: false,
        manageIntegrations: false,
        viewCompanies: false,
        manageCompanies: false,
      }),

      handleSelectAllAdministration: () => apply({
        manageGeneralSettings: 'configure',
        manageDeadlines: true,
        managePolicySettings: true,
        manageUtilisationSettings: true,
        manageTimeEntrySettings: true,
        manageWorkTypeSettings: true,
        manageApprovalsSettings: true,
        manageNotifications: true,
        manageRolesPermissions: true,
        manageScreenDataModes: true,
        manageRates: true,
      }),

      handleDeselectAllAdministration: () => apply({
        manageGeneralSettings: 'view',
        manageDeadlines: false,
        managePolicySettings: false,
        manageUtilisationSettings: false,
        manageTimeEntrySettings: false,
        manageWorkTypeSettings: false,
        manageApprovalsSettings: false,
        manageNotifications: false,
        ...(isAdministrator ? {} : { manageRolesPermissions: false }),
        manageScreenDataModes: false,
        manageRates: false,
      }),
    };
  }, [enabledPermissions, setAllPermissions, onPermissionChange, globalSettings, isAdministrator, auditTrail, postingLog]);
};
